import React, { useEffect, useState } from "react";
import { FaGhost, FaArrowRight } from "react-icons/fa";
import CartItem from "../components/Cards/CartItem";
import "./Cart.css";
import { useNavigate } from "react-router-dom";
import { useProducts } from "../hook/useProducts";
import { PriceTier } from "../types/index";
import { CartSizeItem } from "../pages/ProductSections/ProductItem";
import api from "../api";
import Loader from "../components/Loader";
import ScreenTitle from "../components/ScreenTitle";

const getActivePrice = (item: { priceTiers?: PriceTier[]; cartQuantity?: number }) => {
  const qty   = item.cartQuantity ?? 1;
  const tiers = item.priceTiers ?? [];
  if (tiers.length === 0) return 0;
  const matching = tiers.filter(t => qty >= t.minQty && (t.maxQty === undefined || qty <= t.maxQty));
  if (matching.length > 0)
    return matching.reduce((p, c) => (c.minQty > p.minQty ? c : p)).price;
  return tiers.reduce((p, c) => (p.minQty < c.minQty ? p : c)).price;
};

const Cart: React.FC = () => {
  const navigate = useNavigate();

  const {
    products,
    setProducts,
    loading,
    loadingMore,
    hasMore,
    fetchNextPage,
  } = useProducts("cart");

  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const total = products.reduce((acc, item) => {
      const price = getActivePrice({ priceTiers: item.priceTiers, cartQuantity: item.cartQuantity ?? 1 });
      return acc + price * (item.cartQuantity ?? 1);
    }, 0);
    setTotalPrice(total);
  }, [products]);

  const handleQuantityChange = (productId: number, newQty: number) => {
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, cartQuantity: newQty } : p));
  };

  const handleSizeQuantityChange = async (productId: number, size: string, newQty: number) => {
    setProducts(prev => prev.map(product => {
      if (product.id === productId) {
        const currentCartSizes = product.cartSizes || [];
        const existingCartSize = currentCartSizes.find(cartSize => cartSize.size === size);

        const updatedCartSizes = existingCartSize
          ? currentCartSizes.map(cartSize => 
              cartSize.size === size ? { ...cartSize, quantity: newQty } : cartSize
            )
          : [...currentCartSizes, { size, quantity: newQty, cartItemId: 0 }];

        const totalQuantity = updatedCartSizes.reduce((sum, item) => sum + item.quantity, 0);

        return {
          ...product,
          cartSizes: updatedCartSizes,
          cartQuantity: totalQuantity,
          cartTotal: updatedCartSizes.reduce((sum, item) => sum + (item.quantity * (product.cartPrice || 0)), 0),
        };
      }
      return product;
    }));

    try {
      await api.updateCart(productId.toString(), newQty, size);
    } catch (e) {
      console.error("Failed to update size quantity", e);
    }
  };

  const onBack = () => navigate(-1);

  const handleRemove = async (id: number) => {
    try {
      // Find the product to get all sizes
      const productToRemove = products.find(p => p.id === id);
      if (productToRemove && productToRemove.cartSizes && productToRemove.cartSizes.length > 0) {
        // For sized products, remove all sizes by calling DELETE without size parameter
        // This matches backend logic: if no size provided for sized product, removes all
        await api.removeFromCart(id.toString());
      } else {
        // For non-sized products, just remove the item
        await api.removeFromCart(id.toString());
      }
      setProducts(products.filter(p => p.id !== id));
    } catch (e) {
      console.error("Failed to remove cart item", e);
    }
  };

  if (loading)
    return (
      <div className="cart-loading-state">
        <Loader size="large" type="spinner" text="Updating..." />
      </div>
    );

  return (
    <div className="cart-canvas">
      <ScreenTitle onBack={onBack} getHeaderTitle={() => "Your Bag"} />

      <main className="cart-main-area">
        <div className="items-list-wrapper">
          {products.length === 0 ? (
            <div className="empty-state-card">
              <FaGhost className="ghost-icon" />
              <p>No items in your cart</p>
              <button className="action-btn-black" onClick={onBack}>
                Continue Shopping
              </button>
            </div>
          ) : (
            products.map(product => (
              <div className="cart-item-row" key={product.id}>
                <CartItem
                  onRemove={handleRemove}
                  onQuantityChange={handleQuantityChange}
                  onSizeQuantityChange={handleSizeQuantityChange}
                  product={product}
                />
              </div>
            ))
          )}
        </div>

        {/* Cart sticky bar */}
        {products.length > 0 && (
          <div className="cart-sticky-action-bar">
            <div className="sticky-bar-content">
              <div className="price-display">
                <span className="total-label">Subtotal</span>
                <h2 className="total-amount">₹{totalPrice.toLocaleString("en-IN")}</h2>
              </div>
              <div className="action-btns">
                <button className="cart-sticky-buy-btn" onClick={() => navigate("/checkout")}>
                  Checkout <FaArrowRight />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Cart;